import { useEffect } from 'react';
import { FaMoneyBillWave, FaChartBar } from 'react-icons/fa';

const BasicInfo = ({ formData, handleChange, nextStep, errors }) => {
  useEffect(() => {
    if (formData.loanType === 'Business') {
      if (formData.customerType !== 'Business Owner') {
        handleChange({ 
          target: { name: 'customerType', value: 'Business Owner' } 
        });
      }
    } else if (formData.loanType === 'Personal') {
      if (formData.customerType !== 'Salaried') {
        handleChange({ 
          target: { name: 'customerType', value: 'Salaried' } 
        });
      }
    }
  }, [formData.loanType, formData.customerType, handleChange]);

  // Get loan parameters (interest rate and tenure) based on loan type
  const getLoanParameters = () => {
    const params = {
      'Home': { rate: 8.5, tenure: 20 },
      'Loan Against Property': { rate: 9.5, tenure: 15 },
      'Personal': { rate: 11.5, tenure: 3 },
      'Car': { rate: 9.5, tenure: 5 },
      'Business': { rate: 11, tenure: 5 }
    };
    return params[formData.loanType] || { rate: 8.5, tenure: 20 };
  };

  // Calculate eligible loan amount based on EMI capacity using reverse EMI formula
  const calculateEligibleLoanFromEMI = (emiCapacity, annualRate, years) => {
    if (emiCapacity <= 0) return 0;
    
    const monthlyRate = annualRate / 12 / 100;
    const months = years * 12;
    
    if (monthlyRate === 0) {
      return (emiCapacity * months).toFixed(2);
    }
    
    const numerator = (Math.pow(1 + monthlyRate, months) - 1);
    const denominator = monthlyRate * Math.pow(1 + monthlyRate, months);
    const factor = numerator / denominator;
    
    return (emiCapacity * factor).toFixed(2);
  };

  // Calculate approved loan amount based on EMI capacity
  const calculateApprovedLoan = () => {
    if (!formData.loanType || (!formData.monthlySalary && !formData.netProfit)) return 0;
    
    const emiCapacity = parseFloat(calculateEMICapacity());
    if (emiCapacity <= 0) return 0;
    
    const params = getLoanParameters();
    const eligibleLoan = calculateEligibleLoanFromEMI(emiCapacity, params.rate, params.tenure);
    
    return eligibleLoan;
  };

  // Calculate available EMI capacity based on customer type
  const calculateEMICapacity = () => {
    const mainIncome = formData.customerType === 'Business Owner' 
      ? (parseFloat(formData.netProfit) / 12 || 0) 
      : (parseFloat(formData.monthlySalary) || 0);
      
    const other = parseFloat(formData.otherIncome) || 0;
    const emi = parseFloat(formData.existingEMI) || 0;
      
    if (mainIncome <= 0 && other <= 0) return 0;
    
    if (formData.customerType === 'Business Owner') {
      // Logic for Business: (Annual Net Profit * 80% + Monthly Rental Income * 50%) converted to monthly, minus EMI
      const businessIncome = (parseFloat(formData.netProfit) || 0) * 0.80;
      const rentalIncome = (parseFloat(formData.otherIncome) || 0) * 0.50;
      return Math.max(0, ((businessIncome / 12) + rentalIncome) - emi).toFixed(2);
    }
    
    // Logic for Salaried: (Monthly Salary * 65% + Monthly Rental Income * 50%) minus EMI
    const salaryEMICapacity = (mainIncome * 0.65) + (other * 0.50);
    return Math.max(0, salaryEMICapacity - emi).toFixed(2);
  };

  // Get EMI capacity percentage based on customer type
  const getEMIPercentage = () => {
    return formData.customerType === 'Business Owner' ? 80 : 65;
  };

  const approvedLoan = calculateApprovedLoan();
  const emiCapacity = calculateEMICapacity();

  // Get the appropriate percentage text for loan type
  const getLoanTypePercentage = () => {
    switch (formData.loanType) {
      case 'Home':
        return 65;
      case 'Loan Against Property':
        return 65;
      case 'Car':
        return 65;
      case 'Personal':
        return 65;
      case 'Business':
        return 80;
      default:
        return 65;
    }
  };

  return (
    <div className="form-container">
      <h2>Basic Loan Information</h2>
      <form>
        {/* 1. Full Name */}
        <div className={errors.name ? 'error' : ''}>
          <label>Full Name:</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={(e) => {
              const value = e.target.value.replace(/[^A-Za-z\s]/g, '');
              handleChange({ target: { name: 'name', value } });
            }}
            required
            pattern="[A-Za-z ]+"
            inputMode="text"
          />
          {errors.name && <div className="error-message">{errors.name}</div>}
        </div>

        {/* 2. Phone Number */}
        <div className={errors.phone ? 'error' : ''}>
          <label>Phone Number:</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            pattern="[0-9]{10}"
            maxLength="10"
            inputMode="numeric"
          />
          {errors.phone && <div className="error-message">{errors.phone}</div>}
        </div>

        {/* 3. Email */}
        <div className={errors.email ? 'error' : ''}>
          <label>Email:</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
          {errors.email && <div className="error-message">{errors.email}</div>}
        </div>

        {/* 4. Address */}
        <div className={errors.address ? 'error' : ''}>
          <label>Address:</label>
          <textarea
            name="address"
            value={formData.address}
            onChange={handleChange}
            required
            minLength="10"
          />
          {errors.address && <div className="error-message">{errors.address}</div>}
        </div>

        {/* 5. Loan Type */}
        <div className={errors.loanType ? 'error' : ''}>
          <label>Loan Type:</label>
          <select 
            name="loanType" 
            value={formData.loanType} 
            onChange={handleChange} 
            required
          >
            <option value="">Select Loan Type</option>
            <option value="Home">Home Loan</option>
            <option value="Loan Against Property">Loan Against Property</option>
            <option value="Personal">Personal Loan</option>
            <option value="Car">Car Loan</option>
            <option value="Business">Business Loan</option>
          </select>
          {errors.loanType && <div className="error-message">{errors.loanType}</div>}
        </div>

        {/* 6. Customer Type */}
        {formData.loanType && (
          <div className={`radio-group ${errors.customerType ? 'error' : ''}`}>
            <label>Customer Type:</label>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
              {formData.loanType !== 'Business' && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="radio"
                    name="customerType"
                    value="Salaried"
                    checked={formData.customerType === 'Salaried'}
                    onChange={handleChange}
                    required
                  />
                  Salaried Individual
                </label>
              )}
              {formData.loanType !== 'Personal' && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="radio"
                    name="customerType"
                    value="Business Owner"
                    checked={formData.customerType === 'Business Owner'}
                    onChange={handleChange}
                  />
                  Business Owner
                </label>
              )}
            </div>
            {errors.customerType && <div className="error-message">{errors.customerType}</div>}
          </div>
        )}

        {/* 7. Required Loan Amount */}
        {formData.customerType && (
          <div className={errors.loanAmount ? 'error' : ''}>
            <label>Required Loan Amount (₹):</label>
            <input
              type="number"
              name="loanAmount"
              value={formData.loanAmount}
              onChange={handleChange}
              required
              min="1"
            />
            {errors.loanAmount && <div className="error-message">{errors.loanAmount}</div>}
          </div>
        )}

        {/* Approved Loan Display */}
        {approvedLoan > 0 && formData.loanType && formData.loanAmount && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1.5rem',
            marginTop: '1rem',
            alignItems: 'center'
          }}>
            {/* Left side - Description */}
            <div style={{
              background: '#f5f5f5',
              padding: '1.2rem',
              borderRadius: '10px',
              border: '1px solid #e0e0e0'
            }}>
              <h3 style={{ color: '#333', margin: '0 0 0.8rem 0', fontSize: '1rem', fontWeight: '600' }}>
                Maximum Eligible Loan Amount
              </h3>
              <p style={{ fontSize: '0.9rem', color: '#555', margin: '0', lineHeight: '1.6' }}>
                Based on your income and existing financial obligations, this is the maximum loan amount you can comfortably repay according to our lending criteria.
              </p>
              <div style={{ marginTop: '0.8rem', paddingTop: '0.8rem', borderTop: '1px solid #ddd', fontSize: '0.85rem', color: '#666' }}>
                <p style={{ margin: '0.3rem 0' }}>📊 <strong>How it's calculated:</strong></p>
                <p style={{ margin: '0.3rem 0' }}>
                  {formData.customerType === 'Business Owner' 
                    ? '• 80% of Annual Net Profit + 50% of Rental Income'
                    : '• 65% of Monthly Salary + 50% of Rental Income'}
                </p>
                <p style={{ margin: '0.3rem 0' }}>• Minus: Your existing monthly EMI obligations</p>
              </div>
            </div>

            {/* Right side - Green Box with Amount */}
            <div className="eligible-loan-display" style={{
              background: 'linear-gradient(135deg, #e8f5e8, #c8e6c9)',
              padding: '1.5rem',
              borderRadius: '10px',
              border: '2px solid #66bb6a',
              textAlign: 'center'
            }}>
              <p style={{ fontSize: '0.9rem', color: '#2e7d32', margin: '0 0 0.5rem 0', fontWeight: '500' }}>
                Your Eligible Amount
              </p>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1b5e20', margin: '0' }}>
                ₹ {parseFloat(approvedLoan).toLocaleString('en-IN')}
              </p>
            </div>
          </div>
        )}

        {/* 8. Monthly Salary / Net Profit */}
        {formData.loanType && formData.loanAmount && (
          <>
            <div className={errors.monthlySalary || errors.netProfit ? 'error' : ''}>
              <label>
                {formData.customerType === 'Business Owner' 
                  ? 'Enter Your Annual net profit as per the latest ITR (₹):' 
                  : 'Enter Your Monthly Net Take Home Salary (₹):'}
              </label>
              <input
                type="number"
                name={formData.customerType === 'Business Owner' ? 'netProfit' : 'monthlySalary'}
                value={formData.customerType === 'Business Owner' ? formData.netProfit : formData.monthlySalary}
                onChange={handleChange}
                required
                min="1"
              />
              {(formData.customerType === 'Business Owner' ? errors.netProfit : errors.monthlySalary) && 
                <div className="error-message">{formData.customerType === 'Business Owner' ? errors.netProfit : errors.monthlySalary}</div>}
            </div>

            {/* 9. Other Income */}
            {formData.loanType !== 'Personal' && (
              <div className={errors.otherIncome ? 'error' : ''}>
                  <label>Monthly Rental Income (₹):</label>
                  <input
                    type="number"
                    name="otherIncome"
                    value={formData.otherIncome}
                    onChange={handleChange}
                    min="0"
                  />
                  {errors.otherIncome && <div className="error-message">{errors.otherIncome}</div>}
              </div>
            )}

            {/* 10. Existing EMI */}
            <div className={errors.existingEMI ? 'error' : ''}>
              <label>Existing Monthly EMI Paying (₹):</label>
              <input
                type="number"
                name="existingEMI"
                value={formData.existingEMI}
                onChange={handleChange}
                min="0"
              />
              {errors.existingEMI && <div className="error-message">{errors.existingEMI}</div>}
            </div>
          </>
        )}

        {/* EMI Capacity Display */}
        {emiCapacity > 0 && (formData.monthlySalary || formData.netProfit) && (
          <div className="emi-capacity-display" style={{
            background: 'linear-gradient(135deg, #e3f2fd, #bbdefb)',
            padding: '1rem',
            borderRadius: '10px',
            border: '2px solid #42a5f5',
            marginTop: '1rem'
          }}>
            <h3 style={{ color: '#1565c0', margin: '0 0 0.5rem 0', fontSize: '1.1rem', display: 'flex', alignItems: 'center' }}>
              <FaChartBar style={{ marginRight: '10px' }} /> Available EMI Capacity
            </h3>
            <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0d47a1', margin: '0' }}>
              ₹ {parseFloat(emiCapacity).toLocaleString('en-IN')}
            </p>
            {formData.otherIncome ? (
              <p style={{ fontSize: '0.9rem', color: '#1565c0', margin: '0.5rem 0 0 0' }}>
                {formData.customerType === 'Business Owner' 
                  ? 'Based on: (Annual Net Profit × 80% ÷ 12) + Monthly Rental Income × 50% - Existing EMI'
                  : 'Based on: Monthly Salary × 65% + Monthly Rental Income × 50% - Existing EMI'}
              </p>
            ) : (
              <p style={{ fontSize: '0.9rem', color: '#1565c0', margin: '0.5rem 0 0 0' }}>
                {formData.customerType === 'Business Owner' 
                  ? '80% of your annual net profit available for loan repayment'
                  : '65% of your monthly salary available for loan repayment'}
              </p>
            )}
            <p style={{ fontSize: '0.85rem', color: '#1565c0', margin: '0.5rem 0 0 0' }}>
              Requested Approved Amount: ₹ {parseFloat(approvedLoan).toLocaleString('en-IN')} ✓
            </p>
          </div>
        )}

        <button 
          type="button" 
          onClick={nextStep}
        >
          Next
        </button>
      </form>
    </div>
  );
};

export default BasicInfo;